import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ChevronDown, Volume2 } from 'lucide-react';
import { getWordOfTheDay } from '@/data/wordsOfTheDay';

/** Word of the Day — one inspiring word per local calendar day. */
export default function WordOfTheDay() {
  const w = getWordOfTheDay();
  const [open, setOpen] = useState(false);

  const speak = (e) => {
    e.stopPropagation();
    try {
      const u = new SpeechSynthesisUtterance(w.word);
      u.rate = 0.85;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch { /* speech synthesis unavailable — button is a bonus */ }
  };

  return (
    <motion.button
      type="button"
      data-testid="word-of-the-day"
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.55, ease: 'easeOut' }}
      className="block w-full text-left border border-[#FFD166]/20 bg-gradient-to-br from-[#131B31] to-[#101F1D] p-6 mt-6 hover:border-[#FFD166]/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD166]/40"
    >
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-4 h-4 text-[#FFD166]" />
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#FFD166]/90 font-semibold">Word of the Day</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-white/35 border border-white/10 rounded-full px-2 py-0.5">{w.category}</span>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h3 data-testid="wotd-word" className="text-3xl sm:text-4xl text-white font-bold" style={{ fontFamily: 'var(--font-display)' }}>
          {w.word}
        </h3>
        <span data-testid="wotd-pron" className="text-sm text-[#5EEAD4] font-mono">{w.pron}</span>
        <span data-testid="wotd-pos" className="text-xs italic text-white/50">{w.pos}</span>
        <span
          role="button"
          tabIndex={0}
          data-testid="wotd-speak"
          onClick={speak}
          onKeyDown={(e) => { if (e.key === 'Enter') speak(e); }}
          aria-label={`Hear ${w.word} pronounced`}
          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/5 border border-white/10 text-white/60 hover:text-[#5EEAD4] hover:border-[#5EEAD4]/40 transition-colors"
        >
          <Volume2 className="w-3.5 h-3.5" />
        </span>
      </div>

      <p data-testid="wotd-def" className="mt-2 text-sm sm:text-base text-white/80 leading-relaxed">{w.def}</p>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            data-testid="wotd-details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
              <p className="text-sm text-white/70 italic leading-relaxed">&ldquo;{w.example}&rdquo;</p>
              {w.fact && (
                <p className="text-xs text-[#94A3B8] leading-relaxed">
                  <span className="text-[#FFD166]/80 font-semibold not-italic">Did you know · </span>{w.fact}
                </p>
              )}
              {w.related?.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-white/35 mr-1">Related</span>
                  {w.related.map((r) => (
                    <span key={r} className="px-2.5 py-1 rounded-full text-[11px] bg-[#1C4749]/30 border border-[#5EEAD4]/20 text-[#5EEAD4]/90">{r}</span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-3 flex items-center gap-1 text-[11px] text-white/40">
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        {open ? 'Show less' : 'Tap for example, etymology & related words'}
      </div>
    </motion.button>
  );
}
